<template>

  <div
    class="input-group stihl-ms"
    :class="{ 'is-open': opened, 'is-disabled': disabled }"
    ref="stihlMultiselect"
    :aria-label="selectionLabel"
    @keyup.tab="openMenu"
    @keydown.tab="closeMenu"
    @clickout="closeMenu"
    @keyup.esc.enter="closeMenu"
    @keydown.up.down="moveInMenu"
    aria-multiselectable="true">

    <div class="stihl-ms-toggle-grp">
      <input
        type="text"
        readonly
        :aria-expanded="opened"
        role="combobox"
        ref="toggler"
        aria-label="toggle"
        class="form-control stihl-ms-toggle-input"
        :value="selectionLabel"
        @click.capture="toggleMenu"
        @keyup.esc="closeMenu"
        @keyup.enter="runSearch"
        @keydown.up.down.space.delete.stop="moveInMenu" />
      <div class="stihl-ms-toggle-caret" @click="toggleMenu" @keyup.down="toggleMenu" />
    </div>

    <div class="stihl-ms-menu">

      <div class="stihl-ms-search-grp" v-if="searchable">
        <div class="form-check stihl-ms-input-wrap">
          <img
            alt="search"
            src="@/assets/img/icons/magnifying-glass.svg"
            class="input-left-icon clickable"
            @click="runSearch"
            @keyup.enter="runSearch">
          <input
            type="search"
            ref="searchInput"
            class="stihl-ms-search-input"
            tabindex="-1"
            :placeholder="placeholder"
            v-model="searchFilter"
            @keyup.esc="closeMenu"
            @keyup.enter="searchItems"
            @keydown.up.down.stop="moveInMenu"
            :disabled="!opened"
            aria-label="search" />
        </div>

        <ul class="stihl-ms-found">
          <li
            v-for="(item, i) in foundOptions"
            :key="item[idAttribute]"
            :title="item[idAttribute]"
            class="form-check stihl-ms-menu-item"
            :class="{ 'is-active': i === active }"
            @click="addItem(i)"
            @keyup.enter.stop="addItem(i)"
            @keyup.up.down.stop="moveInMenu">

            <slot name="found" :item="item">
              <span class="stihl-ms-item-text stihl-ms-item-add">{{ item[labelAttribute] }}</span>
            </slot>
          </li>
        </ul>
      </div>

      <div class="stihl-ms-options">
        <label
          v-for="(item, i) in options"
          :key="item[idAttribute]"
          :title="item[idAttribute]"
          class="form-check stihl-ms-menu-item"
          :class="{
            'is-selected': isSelected(item[idAttribute]),
            'is-active': i + foundOptions.length === active,
            'is-read-only': isItemReadOnly(item[idAttribute]),
          }"
          tabindex="0"
          @keyup.up.down.space.stop="moveInMenu">

          <input
            :id="item[idAttribute]"
            type="checkbox"
            tabindex="-1"
            class="stihl-ms-item-input"
            :checked="isSelected(item[idAttribute])"
            :disabled="isItemReadOnly(item[idAttribute])"
            @keyup.esc.enter="closeMenu"
            @click="toggleSelectItem(item[idAttribute], false)"
            aria-label="close"
            role="option">
          <slot name="option" :item="item">
            <span class="stihl-ms-item-text">{{ item[labelAttribute] }}</span>
          </slot>
          <stihl-icon
            icon="trash"
            class="stihl-ms-item-remove clickable"
            alt="remove item"
            @click.prevent="removeItem(i)"
            @keyup.delete="removeItem(i)"
            v-if="searchable && !isItemReadOnly(item[idAttribute])" />
        </label>

        <li v-if="$isEmpty(options)" class="form-check">{{ $t('adm.multiselect.noItems') }}</li>
      </div>

    </div>

  </div>

</template>

<script lang="ts">
// MULTI-SELECT Variant
import 'clickout-event';
import { PropType, defineComponent } from 'vue';
import { Logger } from 'oidc-client-ts';
import StihlIcon from '@/components/ui/StihlIcon.vue';

// option type - used only if searchable
export type SMOption = Record<string, any>;

export default defineComponent({
  name: 'StihlMultiselect',
  components: { StihlIcon },
  emits: ['update:modelValue'],

  props: {
    // format to show the current selection
    selectionFormat: { type: String, default: 'adm.multiselect.selected' },
    selectionFormatSupplier: { type: Function as PropType<(itemsNo: number) => string>, default: null },
    placeholder: { type: String, default: '' },
    modelValue: { type: Object as PropType<string[]>, required: false, default: new Array<string>() },
    // initial options - items visible in the dropdown (on init)
    optionsProvider: { type: Function as PropType<() => Promise<SMOption[]>>, default: null },
    idAttribute: { type: String, default: 'key' }, // identify an item
    labelAttribute: { type: String, default: 'label' }, // display an item
    // function for searching new items (if parameter is empty, is initial search)
    search: { type: Function as PropType<(filter?: string) => Promise<SMOption[]>>, default: null },
    isItemReadOnly: { type: Function as PropType<(key: string) => boolean>, default: () => false },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      selectedItems: [] as string[],
      options: new Array<SMOption>(),
      opened: false,
      active: -1,
      searchFilter: '',
      foundOptions: [] as SMOption[], // items found by search
    };
  },

  computed: {
    selectionLabel() {
      const count = this.selectedItems.length;
      if (this.selectionFormatSupplier) return this.selectionFormatSupplier(count);
      return this.$t(this.selectionFormat, count);
    },
    searchInput() {
      return this.$refs.searchInput as HTMLInputElement;
    },
    togglerInput() {
      return this.$refs.toggler as HTMLInputElement;
    },
    searchable(): boolean {
      return !!this.search;
    },
  },

  methods: {

    openMenu() {
      if (this.disabled) return;

      this.opened = true;
      if (this.searchable) {
        Logger.debug('stihl-ms', ' focus on search-input');
        this.searchFilter = '';
        this.$nextTick(() => this.searchInput.focus());
      } else {
        // this.$nextTick(() => this.togglerInput.focus());
      }
    },
    closeMenu() {
      if (this.opened) {
        this.opened = false;
        this.active = -1;

        if (this.searchable) this.$nextTick(() => this.togglerInput.focus());
      }
    },
    toggleMenu() {
      Logger.debug('stihl-ms', ' toggle', this.opened);
      if (this.opened) this.closeMenu(); else this.openMenu();
    },
    /* onFocusout(e: FocusEvent) {
      if (!(this.$refs.stihlMultiselect as HTMLElement).contains(e.relatedTarget as Node)) {
        this.closeMenu();
      }
    }, */

    runSearch() {
      if (!this.searchable) this.closeMenu();
    },

    searchItems(e: Event) {
      const str = this.searchFilter?.trim();
      if (str) {
        if (!this.search) {
          Logger.error('stihl-ms', ' search method not defined');
        } else {
          this.search(str).then((r) => {
            Logger.debug('stihl-ms', `found ${r.length} items`);
            this.foundOptions = r.filter((x) => !this.options.find((o) => o.id === x.id)); // filter out existing items
          });
        }

        this.searchFilter = '';
        e.stopPropagation();
      }
    },

    /** move item from found-list to options-list (searchable only) */
    addItem(index: number) {
      Logger.debug('stihl-ms', `add ${index} to options`);
      const item = this.foundOptions[index];

      if (this.options.filter((x: SMOption) => x[this.idAttribute] === item[this.idAttribute]).length === 0) {
        this.options.unshift(item); // add to options (to start)
        this.foundOptions.splice(index, 1); // delete from foundOptions

        this.selectedItems.unshift(item[this.idAttribute]);

        this.emitUpdate();
      } else {
        Logger.debug('stihl-ms', `element ${item} already selected`);
      }
    },

    /** move item from options to found-item (searchable only) */
    removeItem(index: number) {
      Logger.debug('stihl-ms', ' removeItem:', index);
      const item = this.options[index];

      this.options.splice(index, 1); // remove item from options
      this.foundOptions.push(item); // add back to foundOptions
      this.removeFromSelected(item[this.idAttribute]);

      this.emitUpdate();
    },

    /** move the cursor in menu */
    moveInMenu(e: KeyboardEvent) {
      Logger.debug('stihl-ms', 'BEFORE key=', e.key, ' active=', this.active);

      const total = this.options.length + this.foundOptions.length;

      if (e.key === 'ArrowDown') {
        this.opened = true;
        if (this.active + 1 < total) {
          if (this.active < 0 && this.searchable) this.togglerInput.focus();
          this.active++;
        }
      } else if (e.key === 'ArrowUp') {
        if (this.active >= 0) {
          this.active--;
          if (this.active < 0 && this.searchable) this.searchInput.focus();
        }
      } else if (e.key === 'Delete') {
        const idx = this.active - this.foundOptions.length;
        if (idx >= 0 && idx < this.options.length) this.removeItem(idx);
      } else if (e.key === ' ') {
        if (this.active < this.foundOptions.length) {
          // add found-item to options
          this.addItem(this.active);
        } else {
          // select/deselect item
          const idx = this.active - this.foundOptions.length;
          const itemKey = this.options[idx][this.idAttribute];
          this.toggleSelectItem(itemKey, false);
        }
      }

      Logger.debug('stihl-ms', ' AFTER key=', e.key, ' active=', this.active);
      return false;
    },

    scrollIntoView() {
      this.$nextTick(() => {
        const activeItems = document.getElementsByClassName('stihl-ms-menu-item is-active');
        Logger.debug('stihl-ms', ' scrollIntoView activeItems:', activeItems);
        for (let i = 0; i < activeItems.length; i++) {
          const el = activeItems[i];
          Logger.debug('stihl-ms', ' activeItem: ', el.textContent);
          el.scrollIntoView();
        }
      });
    },

    /** select/unselect item */
    toggleSelectItem(key: string, close: boolean = true) {
      if (this.isItemReadOnly(key)) return;

      Logger.debug('stihl-ms', ' toggleSelectItem:', key);
      // find index of item in selected
      const i = this.selectedItems ? this.selectedItems.indexOf(key) : -1;
      if (i > -1) {
        this.removeFromSelected(key);
      } else {
        this.addToSelected(key);
      }

      this.emitUpdate();

      if (close) this.closeMenu();
    },

    addToSelected(key: string) {
      this.selectedItems.push(key);
      Logger.debug('stihl-ms', ` added ${key} to selected`);
    },

    removeFromSelected(key: string) {
      const i = this.selectedItems ? this.selectedItems.indexOf(key) : -1;
      if (i > -1) {
        // remove from selected
        this.selectedItems.splice(i, 1);
        Logger.debug('stihl-ms', ` removed ${key} from selected`);
      }
    },

    emitUpdate() {
      Logger.debug('stihl-ms', ' newValue:', JSON.stringify(this.selectedItems));
      this.$emit('update:modelValue', this.selectedItems);
      this.$nextTick(() => this.togglerInput.focus());
    },

    isSelected(key: string): boolean {
      const val = this.selectedItems && this.selectedItems.indexOf(key) > -1;
      Logger.debug('stihl-ms', `isSelected(${key}):${val}`);
      return val;
    },
  },

  watch: {
    active() {
      // each time the active element changes we have to bring it into view
      this.scrollIntoView();
    },
  },

  async mounted() {
    // Logger.info('stihl-ms', 'mounted value:', JSON.stringify(this.modelValue));

    if (this.optionsProvider) {
      this.options = await this.optionsProvider();
      Logger.debug('stihl-ms', `init: ${this.options.length} options`);
    }
    this.selectedItems = this.modelValue;
  },
});
</script>
